import { Dispatch, SetStateAction, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps, VisibilitiesRef } from './types';
import {
  FieldDesc,
  AllFieldValueTypes,
  StepDesc,
  DeclarationContext,
  RepeatableGroupDesc,
} from '90.quickConnect.Models/models';
import updateVisibilityFieldOrChild from '20.formLib/helpers/updateField/updateVisibilityFieldOrChild';
import updateErrorsFieldOrChild from '20.formLib/helpers/updateField/updateErrorsFieldOrChild';
import updateValueFieldOrChild from '20.formLib/helpers/updateField/updateValueFieldOrChild';
import { FieldType, FormType } from '90.quickConnect.Models/enums';
import updateStatedIdFieldOrChild from '20.formLib/helpers/updateField/updateStatedIdFieldOrChild';
import { useStore } from '30.quickConnect.Stores';
import { flatten } from '80.quickConnect.Core/helpers';
import useEffectOnce from '80.quickConnect.Core/hooks/useEffectOnce';
import useFormEventQCScript from '10.quickConnect.app/components/domain/Declaration/hooks/useFormEventQCScript';
import updateReadOnlyFieldOrChild from '20.formLib/helpers/updateField/updateReadOnlyFieldOrChild';
import updateMandatoryFieldOrChild from '20.formLib/helpers/updateField/updateMandatoryFieldOrChild';
import updateItemsFieldOrChild from '20.formLib/helpers/updateField/updateItemsFieldOrChild';
import { QCSFunctions } from '20.formLib/DeclarationContainer/contexts/types';
import { updatePropertyFieldOrChild } from '20.formLib/helpers/updateField/updatePropertyFieldOrChild';
import {
  parseRepeatableGroupCommand,
  updateRepeatableGroupByCommand as updateRepeatableGroupByCommandHelper,
} from '20.formLib/helpers/repeatableGroup/mutations';

const useData = (
  context: DeclarationContext,
  setDeclaration: Dispatch<SetStateAction<FieldDesc[] | undefined>>,
  declaration: FieldDesc[],
  formType: FormType,
  isDraft: boolean,
  isEditable: boolean,
  qcScriptObj?: string,
): UseDataProps => {
  const [canSubmit, setCanSubmit] = useState(false);
  const visibilitiesRef = useRef<VisibilitiesRef>(new Map());
  const scriptForcedVisibilitiesRef = useRef<Set<string>>(new Set());
  const qcsFormEvent = useFormEventQCScript();

  const {
    DeclarationStore: { markEntryModified },
    QCScriptStore: {
      setControllerMethod,
      setQCSFieldId,
      setQCSBaseFieldId,
      setFlattenFieldsByFullPathId,
      setFlattenFields,
    },
    EquipmentsStore: { findIsInDataSource },
  } = useStore();

  const { t: declarationTranslator } = useTranslation('declaration');

  const hasControllerMethod = useCallback((field: FieldDesc): boolean => {
    const { controller } = field;
    if (controller === undefined) return false;

    return controller !== '';
  }, []);

  const canUpdate = useCallback(
    (updatedFieldFullPathId: string, newValue: AllFieldValueTypes): boolean => {
      const flattenFields = flatten(declaration, (f: FieldDesc) => f.items);
      const fieldToCheck = flattenFields.find(
        (f: FieldDesc) => updatedFieldFullPathId.toLowerCase() === f.fullPathId.toLowerCase(),
      );
      if (!fieldToCheck) return true;
      const canUpdateBool: boolean = fieldToCheck.value === newValue ? hasControllerMethod(fieldToCheck) : true;

      if (canUpdateBool) {
        // On set l'observable controllerMethod afin d'avoir la methode a appeler pour les QCSCripts
        const controllerMethod = fieldToCheck.controller ?? '';

        // Prendre en compte le desactivatedAutomation
        // eslint-disable-next-line prefer-destructuring
        const desactivatedAutomation = fieldToCheck.desactivatedAutomation;

        setFlattenFieldsByFullPathId(updatedFieldFullPathId.toLowerCase(), newValue);
        if (controllerMethod && controllerMethod !== '' && !desactivatedAutomation) {
          setControllerMethod(controllerMethod);
          setQCSBaseFieldId(updatedFieldFullPathId);
          setQCSFieldId(fieldToCheck.id);
        }
      }

      return canUpdateBool;
    },
    [
      declaration,
      hasControllerMethod,
      setControllerMethod,
      setQCSBaseFieldId,
      setQCSFieldId,
      setFlattenFieldsByFullPathId,
    ],
  );

  const updateDeclaration = useCallback(
    (updatedFieldFullPathId: string, newValue: AllFieldValueTypes) => {
      markEntryModified();
      if (canUpdate(updatedFieldFullPathId, newValue) || findIsInDataSource(updatedFieldFullPathId) === false) {
        setDeclaration((prev) => {
          if (prev && prev.length > 0) {
            prev.forEach((root) => {
              updatedFieldFullPathId = updatedFieldFullPathId.toLowerCase();
              updateValueFieldOrChild(updatedFieldFullPathId, root, newValue);
            });
            return [...prev];
          }
          return prev;
        });
      }
    },
    [markEntryModified, setDeclaration, canUpdate, findIsInDataSource],
  );

  const isTheSameError = useCallback(
    (mandatoryFieldFullPathId: string, newErrors: string[]): boolean => {
      const field = flatten(declaration, (i) => i.items).find(
        ({ fullPathId: fullId }: FieldDesc) => fullId === mandatoryFieldFullPathId,
      );

      if (!field) return false;

      const { errors } = field;

      if (!errors) return false;

      if (errors.length === 0 || errors.length !== newErrors.length) return false;

      return errors.every((error: string) => newErrors.includes(error));
    },
    [declaration],
  );

  const updateFieldErrors = useCallback(
    (mandatoryFieldFullPathId: string, newErrors: string[]) => {
      if (!isTheSameError(mandatoryFieldFullPathId, newErrors)) {
        setDeclaration((prev) => {
          if (prev && prev.length > 0) {
            prev.forEach((root) => {
              updateErrorsFieldOrChild(
                mandatoryFieldFullPathId,
                root,
                newErrors,
                declarationTranslator('errors_invalid_field_items'),
              );
            });
            return [...prev];
          }
          return prev;
        });
      }
    },
    [isTheSameError, setDeclaration, declarationTranslator],
  );

  const isTheSameVisibility = useCallback(
    (hidableFieldFullPathId: string, newVisibility: boolean, forcedByScript = false): boolean => {
      if (!forcedByScript && scriptForcedVisibilitiesRef.current.has(hidableFieldFullPathId.toLowerCase())) {
        return true;
      }

      const lastVisibilityUpdate = visibilitiesRef.current.get(hidableFieldFullPathId);

      // Use the latest requested visibility first to avoid race conditions with async React state updates.
      if (lastVisibilityUpdate !== undefined) return lastVisibilityUpdate === newVisibility;

      const flattenFields = flatten(declaration, (f: FieldDesc) => f.items);
      const fieldToCheck = flattenFields.find((f: FieldDesc) => f.fullPathId === hidableFieldFullPathId);
      if (!fieldToCheck) return false;
      return fieldToCheck.isVisible === newVisibility;
    },
    [declaration],
  );

  const updateVisibilityRef = useCallback((hidableFieldFullPathId: string, newVisibility: boolean) => {
    visibilitiesRef.current.set(hidableFieldFullPathId, newVisibility);
  }, []);

  // Oublie la derniere visibilite demandee pour un chemin dont le champ a disparu (ex: ligne de groupe repetable supprimee),
  // sinon un nouveau champ cree au meme fullPathId heriterait de cette valeur et sa mise a jour serait ignoree.
  const clearFieldVisibilityCache = useCallback((hidableFieldFullPathId: string) => {
    visibilitiesRef.current.delete(hidableFieldFullPathId);
  }, []);

  const updateVisibilityForcedByScriptRef = useCallback((hidableFieldFullPathId: string, forcedByScript = false) => {
    const normalizedFieldPath = hidableFieldFullPathId.toLowerCase();

    if (forcedByScript) {
      scriptForcedVisibilitiesRef.current.add(normalizedFieldPath);
    }
  }, []);

  const updateFieldVisibility = useCallback(
    (hidableFieldFullPathId: string, newVisibility: boolean, forcedByScript = false) => {
      if (isTheSameVisibility(hidableFieldFullPathId, newVisibility, forcedByScript)) {
        updateVisibilityRef(hidableFieldFullPathId, newVisibility);
        updateVisibilityForcedByScriptRef(hidableFieldFullPathId, forcedByScript);
        return;
      }

      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((root) => {
            updateVisibilityFieldOrChild(hidableFieldFullPathId, root, newVisibility);
          });
          return [...prev];
        }
        return prev;
      });
      updateVisibilityRef(hidableFieldFullPathId, newVisibility);
      updateVisibilityForcedByScriptRef(hidableFieldFullPathId, forcedByScript);
    },
    [isTheSameVisibility, setDeclaration, updateVisibilityRef, updateVisibilityForcedByScriptRef],
  );

  const updateFieldReadOnly = useCallback(
    (readOnlyFieldFullPathId: string, isReadOnly: boolean) => {
      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((root) => {
            updateReadOnlyFieldOrChild(readOnlyFieldFullPathId, root, isReadOnly);
          });
          return [...prev];
        }
        return prev;
      });
    },
    [setDeclaration],
  );

  const updateFieldMandatory = useCallback(
    (
      mandatoryFieldFullPathId: string,
      isMandatory: boolean,
      updateFieldErrorsCaller: (mandatoryFieldFullPathId: string, newErrors: string[]) => void,
    ) => {
      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((root) => {
            // Suppression du message d'erreurs dans le cas où le mandatory est set sur false, car sinon on ne pourrait jamais valider
            const messageToRemove: string | undefined = !isMandatory
              ? declarationTranslator('errors_mandatory_field').toString()
              : undefined;

            updateMandatoryFieldOrChild(
              mandatoryFieldFullPathId,
              root,
              isMandatory,
              updateFieldErrorsCaller,
              messageToRemove,
            );
          });
          return [...prev];
        }
        return prev;
      });
    },
    [setDeclaration, declarationTranslator],
  );

  const updateItemsForThisField = useCallback(
    (fullPathId: string, nextItems: FieldDesc[]) => {
      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((root) => {
            updateItemsFieldOrChild(fullPathId, root, nextItems);

            nextItems.forEach((nextItem: FieldDesc) => {
              const { items, fullPathId: fullPathIdItem } = nextItem;
              updateItemsForThisField(fullPathIdItem, items);
            });
          });
          return [...prev];
        }
        return prev;
      });
    },
    [setDeclaration],
  );

  const updateRepeatableGroupByCommand = useCallback(
    (repeatableGroupFullPathId: string, commandAsString: string): boolean => {
      // Le bool de retour sert au routage (skip updateDeclaration), il doit etre deterministe.
      const parsedCommand = parseRepeatableGroupCommand(commandAsString);
      if (!parsedCommand) return false;

      setDeclaration((prev) => {
        if (!prev || prev.length === 0) return prev;

        const flattenFields = flatten(prev, (f: FieldDesc) => f.items);
        const repeatableGroup = flattenFields.find(
          (f: FieldDesc) =>
            f.fieldType === FieldType.RepeatableGroup &&
            f.fullPathId.toLowerCase() === repeatableGroupFullPathId.toLowerCase(),
        ) as RepeatableGroupDesc | undefined;

        if (!repeatableGroup) return prev;

        const { nextItems, changed } = updateRepeatableGroupByCommandHelper(repeatableGroup, context, commandAsString);

        if (!changed) return prev;

        // Remplacement structurel des lignes du groupe dans la declaration
        prev.forEach((root) => {
          updateItemsFieldOrChild(repeatableGroup.fullPathId.toLowerCase(), root, nextItems);
        });

        const nextDeclaration = [...prev];
        // Le flatten global doit suivre la nouvelle structure pour les prochains scripts/validations.
        setFlattenFields(flatten(nextDeclaration, (f: FieldDesc) => f.items));
        return nextDeclaration;
      });

      return true;
    },
    [context, setDeclaration, setFlattenFields],
  );

  const forceFieldUpdate = useCallback(
    (fieldPathIdToUpdate: string) => {
      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((root) => {
            updateStatedIdFieldOrChild(fieldPathIdToUpdate, root);
          });
          return [...prev];
        }
        return prev;
      });
    },
    [setDeclaration],
  );

  const steps = useMemo(
    () => (formType === FormType.Workflow ? (declaration as StepDesc[]) : []),
    [declaration, formType],
  );

  // Initialiser les flattenFields du QCScriptStore avant les effects
  // Ainsi les Resolvers (effects enfants) mettent à jour le store MobX via setFlattenFieldsByFullPathId,
  // et l'initialization() (effect parent) ne les écrase plus
  useMemo(() => {
    if (qcScriptObj && qcScriptObj !== '' && isEditable) {
      setFlattenFields(flatten(declaration, (f: FieldDesc) => f.items));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffectOnce(() => {
    // Si un QCScript existe
    if (qcScriptObj && qcScriptObj !== '' && isEditable && qcsFormEvent) {
      const { initialization, onFormLoaded } = qcsFormEvent;
      initialization(
        context,
        qcScriptObj,
        updateDeclaration,
        updateFieldVisibility,
        updateFieldErrors,
        updateFieldReadOnly,
        updateFieldMandatory,
      );
      onFormLoaded(flatten(declaration, (f: FieldDesc) => f.items));
    }
  });

  const qcsFunctions: QCSFunctions = useMemo(
    () => ({
      updateDeclaration,
      updateRepeatableGroupByCommand,
      updateFieldVisibility,
      updateFieldErrors,
      updateFieldReadOnly,
      updateFieldMandatory,
    }),
    [
      updateDeclaration,
      updateRepeatableGroupByCommand,
      updateFieldVisibility,
      updateFieldErrors,
      updateFieldReadOnly,
      updateFieldMandatory,
    ],
  );

  const updateProperty = useCallback(
    (fullPathIdPropertyField: string, property: string, propertyValue: unknown) => {
      setDeclaration((prev) => {
        if (prev && prev.length > 0) {
          prev.forEach((field) => {
            updatePropertyFieldOrChild(fullPathIdPropertyField, field, property, propertyValue);
          });
          return [...prev];
        }
        return prev;
      });
    },
    [setDeclaration],
  );

  return {
    setCanSubmit,
    canSubmit,
    updateDeclaration,
    updateFieldVisibility,
    clearFieldVisibilityCache,
    updateFieldReadOnly,
    updateFieldErrors,
    forceFieldUpdate,
    updateItemsForThisField,
    steps,
    qcsFormEvent,
    qcsFunctions,
    updateProperty,
  };
};

export default useData;
